import { c as _c } from "react/compiler-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import RatioContainer from 'components/consumer/RatioContainer';
import PDPDesktopHeroGallery from '../PDPDesktopHeroGallery';
import PDPDesktopGalleryRevamp from '../PDPDesktopGalleryRevamp';
import PDPKeySellingPoints from '../PDPKeySellingPoints';
import PDPDesktopMarxentConfigurator from '../../modular-configurator/PDPMarxentConfigurator';
import useIsGalleryRevampVariant from '../useIsGalleryRevampVariant';
import usePDPDeepLinkVariantMismatch from '../hooks/usePDPDeepLinkVariantMismatch';
import { getPaintedHero } from '../PDPDesktopHeroGallery/helpers/lastPaintedHero';
import buildPdpDesktopHeroLcpUrl from '../PDPDesktopHeroGallery/helpers/buildPdpDesktopHeroLcpUrl';

// Re-show the last painted hero instead of blanking. A suspension here lands on
// an already-painted gallery (a late userLocation re-keys every location-scoped
// query at once), so there is a hero to hold (CON-8543). On the first load
// nothing has painted yet, so use the server hero that has already resolved.
// Exported for layouts that render the gallery themselves.
const heroImageShape = {
  src: PropTypes.string.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  alt: PropTypes.string
};
export const PDPDesktopGalleryHoldFallback = t0 => {
  const $ = _c(16);
  const {
    tag,
    productHeroImage: t1,
    productCylindoHeroImage: t2
  } = t0;
  const productHeroImage = t1 === undefined ? null : t1;
  const productCylindoHeroImage = t2 === undefined ? null : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getPaintedHero();
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const paintedSrc = t3;
  const t4 = productCylindoHeroImage?.defaultUpholsteryValue;
  const t5 = productCylindoHeroImage?.defaultOrientation;
  let t6;
  if ($[1] !== t4 || $[2] !== t5) {
    t6 = {
      defaultUpholsteryValue: t4,
      defaultOrientation: t5
    };
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const isDeepLinkMismatch = usePDPDeepLinkVariantMismatch(t6);
  const serverSrc = isDeepLinkMismatch ? null : productHeroImage?.src || productCylindoHeroImage?.src || null;
  const heldSrc = paintedSrc || serverSrc;
  let t7;
  if ($[4] !== heldSrc) {
    t7 = heldSrc ? buildPdpDesktopHeroLcpUrl(heldSrc) ?? heldSrc : null;
    $[4] = heldSrc;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const src = t7;
  if (tag === "ksp") {
    let t8;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="min-h-[90px]" />;
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    return t8;
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      maxWidth: "calc((100vh - 194px) / 0.6666666666666666)"
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== productCylindoHeroImage?.alt || $[9] !== productHeroImage?.alt || $[10] !== productHeroImage?.height || $[11] !== productHeroImage?.width || $[12] !== src) {
    t9 = src ? <img className="h-full w-full object-contain object-center select-none" src={src} alt={paintedSrc ? "" : productHeroImage?.alt || productCylindoHeroImage?.alt || ""} aria-hidden={paintedSrc ? "true" : undefined} width={productHeroImage?.width || 1600} height={productHeroImage?.height || 2400} loading="eager" fetchPriority="high" decoding="async" /> : null;
    $[8] = productCylindoHeroImage?.alt;
    $[9] = productHeroImage?.alt;
    $[10] = productHeroImage?.height;
    $[11] = productHeroImage?.width;
    $[12] = src;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <div className="mb-8 ml-auto mr-auto relative" style={t8}><RatioContainer ratio={0.6666666666666666}>{t9}</RatioContainer></div>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
PDPDesktopGalleryHoldFallback.propTypes = {
  tag: PropTypes.string,
  productHeroImage: PropTypes.shape(heroImageShape),
  productCylindoHeroImage: PropTypes.shape(heroImageShape)
};
const PDPDesktopPhotoPanelInner = t0 => {
  const $ = _c(13);
  const {
    isEditOrder: t1,
    pdpIsModularBYOProduct: t2,
    slug,
    hideMarxent: t3,
    unavailableFabric: t4,
    productHeroImage: t5,
    productCylindoHeroImage: t6
  } = t0;
  const isEditOrder = t1 === undefined ? false : t1;
  const pdpIsModularBYOProduct = t2 === undefined ? false : t2;
  const hideMarxent = t3 === undefined ? true : t3;
  const unavailableFabric = t4 === undefined ? false : t4;
  const productHeroImage = t5 === undefined ? null : t5;
  const productCylindoHeroImage = t6 === undefined ? null : t6;
  let t7;
  if ($[0] !== hideMarxent || $[1] !== isEditOrder || $[2] !== pdpIsModularBYOProduct || $[3] !== productCylindoHeroImage || $[4] !== productHeroImage || $[5] !== slug || $[6] !== unavailableFabric) {
    t7 = pdpIsModularBYOProduct ? <PDPDesktopMarxentConfigurator pdpIsModularBYOProduct={pdpIsModularBYOProduct} slug={slug} key={slug} hideMarxent={hideMarxent} unavailableFabric={unavailableFabric} /> : <PDPDesktopGalleryVariant isEditOrder={isEditOrder} pdpIsModularBYOProduct={pdpIsModularBYOProduct} slug={slug} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />;
    $[0] = hideMarxent;
    $[1] = isEditOrder;
    $[2] = pdpIsModularBYOProduct;
    $[3] = productCylindoHeroImage;
    $[4] = productHeroImage;
    $[5] = slug;
    $[6] = unavailableFabric;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== isEditOrder) {
    t8 = !isEditOrder && <SuspenseWithLoader fallback={<PDPDesktopGalleryHoldFallback tag="ksp" />}><PDPKeySellingPoints /></SuspenseWithLoader>;
    $[8] = isEditOrder;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7 || $[11] !== t8) {
    t9 = <div className="sticky top-8">{t7}{t8}</div>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
};
PDPDesktopPhotoPanelInner.propTypes = {
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  slug: PropTypes.string,
  hideMarxent: PropTypes.bool,
  unavailableFabric: PropTypes.bool,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
const PDPDesktopGalleryVariant = t0 => {
  const $ = _c(7);
  const {
    isEditOrder,
    pdpIsModularBYOProduct,
    slug,
    productHeroImage,
    productCylindoHeroImage
  } = t0;
  const isGalleryRevampVariant = useIsGalleryRevampVariant(!pdpIsModularBYOProduct && !isEditOrder);
  let t1;
  if ($[0] !== isEditOrder || $[1] !== isGalleryRevampVariant || $[2] !== pdpIsModularBYOProduct || $[3] !== productCylindoHeroImage || $[4] !== productHeroImage || $[5] !== slug) {
    t1 = isGalleryRevampVariant ? <SuspenseWithLoader fallback={<PDPDesktopGalleryHoldFallback tag="revamp" productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}><PDPDesktopGalleryRevamp slug={slug} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /></SuspenseWithLoader> : <SuspenseWithLoader fallback={<PDPDesktopGalleryHoldFallback tag="gallery" productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}><PDPDesktopHeroGallery isEditOrder={isEditOrder} pdpIsModularBYOProduct={pdpIsModularBYOProduct} slug={slug} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /></SuspenseWithLoader>;
    $[0] = isEditOrder;
    $[1] = isGalleryRevampVariant;
    $[2] = pdpIsModularBYOProduct;
    $[3] = productCylindoHeroImage;
    $[4] = productHeroImage;
    $[5] = slug;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
PDPDesktopGalleryVariant.propTypes = {
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};

// Outermost boundary: with no fallback this renders null, so a suspension in
// PDPDesktopPhotoPanelInner's own render, above the per-child boundaries,
// would blank hero, thumbnails and selling points together.
const PDPDesktopPhotoPanel = props => {
  const $ = _c(8);
  let t0;
  if ($[0] !== props.productCylindoHeroImage || $[1] !== props.productHeroImage) {
    t0 = <PDPDesktopGalleryHoldFallback tag="panel" productHeroImage={props.productHeroImage} productCylindoHeroImage={props.productCylindoHeroImage} />;
    $[0] = props.productCylindoHeroImage;
    $[1] = props.productHeroImage;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props) {
    t1 = <PDPDesktopPhotoPanelInner {...props} />;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = <SuspenseWithLoader fallback={t0}>{t1}</SuspenseWithLoader>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export default PDPDesktopPhotoPanel;